/**
 * Runtime schémata nastavení úvodní stránky (zod). Přestěhovaná z `types/homepage.ts`
 * (řez 13, Kontrakt 4(a)), aby `types/**` za běhu na zod nedosáhl. Typy stejných jmen
 * a defaulty bez zod (`DEFAULT_PROMO_BAR`, `DEFAULT_TRIP_TYPE_IMAGE_IDS`) zůstávají
 * v `types/homepage.ts`.
 */
import { z } from "zod";
import { isKnownPublicPath } from "../routes";
import { DEFAULT_PROMO_BAR, DEFAULT_TRIP_TYPE_IMAGE_IDS } from "../types/homepage";
// Jen TYP (import se při překladu vymaže), takže cyklus s `types/index.ts` nevzniká.
import type { TripType } from "../types";

/**
 * Editable homepage content, persisted in the `homepage` settings category.
 * Shared by the admin editor (write), the PUT validator (API), and the public
 * /api/homepage route (read) so the wire contract lives in exactly one place.
 */
export const HitRefSchema = z.object({
  type: z.enum(["trip", "activity"]),
  id: z.string(),
});
export const DestRefSchema = z.object({
  id: z.string(),
  size: z.enum(["large", "small"]).optional(),
});
/**
 * Promo lišta nad headerem webu — řízená adminem, default vypnutá.
 * Interní href se validuje proti centrální route mapě (externí https odkazy projdou).
 */
export const PromoBarSettingsSchema = z.object({
  enabled: z.boolean(),
  text: z.string().max(200),
  href: z
    .string()
    .max(500)
    .refine((v) => v === "" || /^https?:\/\//.test(v) || isKnownPublicPath(v), {
      message: "Unknown internal path (not in the route map)",
    }),
});
/**
 * Shovívavé čtení uložené lišty: hodnota, která neprošla přísným schématem, se NESMÍ
 * tiše vyměnit za default — typicky `href` míří na cestu, která mezitím zmizela z route
 * mapy, ale kampaň na webu pořád běží. Kdyby GET vrátil prázdný default, editor uvidí
 * prázdný formulář a první uložení běžící kampaň přepíše.
 *
 * Uvolňují se proto REDAKČNÍ pravidla (délka, route mapa) — pole zůstávají povinná jen
 * typově a `.catch()` doplní default u toho, které v DB chybí nebo má jiný typ.
 *
 * POZOR na hranici, kterou `.extend()` DRŽÍ a kterou NE: přenáší se seznam polí, ale ne
 * shovívavost. Čtvrté pole přidané výš se sem propíše s PŘÍSNÝM pravidlem a bez
 * `.catch()`, takže by jedna neznámá hodnota shodila `safeParse` celé lišty — GET by
 * vrátil `DEFAULT_PROMO_BAR`, editor prázdný formulář a první uložení by přepsalo běžící
 * kampaň, tedy přesně to, čemu má tahle cesta bránit. Každé pole proto MUSÍ dostat řádek
 * níž; vymáhá to `homepage.test.ts` (nakrmí každé pole nesmyslem a čeká úspěšný parse),
 * ne komentář. Automaticky to udělat nejde: shovívavá varianta je „holý typ + `.catch()`",
 * a `.max()`/`.refine()` z hotového schématu spolehlivě odloupnout neumíme.
 *
 * Dřív tuhle roli hrála ruční kopie výčtu polí v API (`salvagePromoBar`) — a ta by nové
 * pole zahodila přesně tam, kde ztráta bolí nejvíc.
 */
export const PromoBarSettingsLenientSchema = PromoBarSettingsSchema.extend({
  enabled: z.boolean().catch(DEFAULT_PROMO_BAR.enabled),
  text: z.string().catch(DEFAULT_PROMO_BAR.text),
  href: z.string().catch(DEFAULT_PROMO_BAR.href),
});

/**
 * Fotky tří karet typů na úvodní stránce (vize H2). Psané jako `Record<TripType, …>`,
 * aby čtvrtý typ nešlo zapomenout — týž trik, jakým `TRIP_TYPE_PATH_SLUGS` vynucuje
 * úplnost slugů.
 */
export const TripTypeImageIdsSchema = z.object({
  guided: z.string().nullable(),
  private: z.string().nullable(),
  resort: z.string().nullable(),
}) satisfies z.ZodType<Record<TripType, string | null>>;
/**
 * Shovívavé čtení uložených fotek — TÁŽ role a týž tvar jako u promo lišty výš, ze
 * stejného důvodu: `z.object` je „všechno nebo nic", takže JEDNA vadná (nebo chybějící)
 * hodnota by shodila `safeParse` celého objektu a veřejná routa by se vrátila ke sdílenému
 * defaultu — VČETNĚ karet, které operátor vědomě vyprázdnil. To je přímý rozpor s hranicí
 * fallbacku popsanou o odstavec výš (hranice je ŘÁDEK, ne pole).
 *
 * Nejde o teoretický případ: čtvrtý `TripType` (schéma výš s ním počítá) přibude do
 * schématu dřív, než se přepíšou uložené řádky, a ty pak mají klíče tři. Stejně dopadne
 * ručně editovaný řádek v D1.
 *
 * Každé pole proto MUSÍ mít vlastní `.catch()`; vymáhá to `homepage.test.ts` (nakrmí každé
 * pole nesmyslem a čeká úspěšný parse), ne tenhle komentář.
 */
export const TripTypeImageIdsLenientSchema = TripTypeImageIdsSchema.extend({
  guided: z.string().nullable().catch(DEFAULT_TRIP_TYPE_IMAGE_IDS.guided),
  private: z.string().nullable().catch(DEFAULT_TRIP_TYPE_IMAGE_IDS.private),
  resort: z.string().nullable().catch(DEFAULT_TRIP_TYPE_IMAGE_IDS.resort),
});

/**
 * Meze kurátorovaných seznamů homepage (vize F12.3). Veřejné `GET /api/homepage` načítá
 * položky `inArray` nad uloženým seznamem, takže jeho délku musí držet jediná zápisová
 * cesta (`PUT /api/admin/settings/homepage` → `.partial()` tohoto schématu). Meze jsou
 * ≥ 2× produkčního maxima k 3. 10. 2026 (4 tipy, 7 oblíbených destinací).
 */
export const HOMEPAGE_MONTHLY_HITS_MAX = 12;
export const HOMEPAGE_POPULAR_DESTINATIONS_MAX = 20;

export const HomepageSettingsSchema = z.object({
  heroTitle: z.string().max(200),
  heroSubtitle: z.string().max(300),
  heroBackgroundImageId: z.string().nullable(),
  /**
   * Úvodní fotka + citát stránky `/o-nas` (řez 11). Kategorie `homepage` je fakticky
   * bucket „editovatelný obsah webu" (žije v ní i promoBar renderovaný všude), takže
   * pro tohle nevzniká nová kategorie. Bez obrázku se hero na `/o-nas` nevykreslí.
   */
  aboutHeroImageId: z.string().nullable(),
  aboutHeroQuote: z.string().max(400),
  monthlyHits: z.array(HitRefSchema).max(HOMEPAGE_MONTHLY_HITS_MAX),
  popularDestinations: z.array(DestRefSchema).max(HOMEPAGE_POPULAR_DESTINATIONS_MAX),
  promoBar: PromoBarSettingsSchema,
  /** Fotky karet sekce „Zážitky podle vašich představ" (vize H2). */
  tripTypeImageIds: TripTypeImageIdsSchema,
});
